import { useMemo } from "react";
import { useMedia } from "@rapid/contexts/MediaContext";
import { useAutoPredictionPending } from "@rapid/contexts/AutoPredictionsContext";
import { useCurrentMedia, useMediaStore } from "@rapid/store";
import { getExtractedFrameIdsForMediaItem } from "@rapid/hooks/useMediaUtils";
import { MediaItemStatus } from "@rapid/types/media";
import { useIsGeneratingPredictions } from "@rapid/hooks/useIsGeneratingPredictions";
import { useFlowState } from "@rapid/hooks/useFlowState";
import { BuildSteps, Flows } from "@rapid/types/flows";

export const useIsWaitingForPredictions = (isExtractingWithAI: boolean) => {
    const { step, flow } = useFlowState();
    const isAutoPredictionPending = useAutoPredictionPending();
    const currentMedia = useCurrentMedia();
    const { pendingMediaItems, mediaItems } = useMedia();
    const media = useMediaStore();
    const isGeneratingPredictions = useIsGeneratingPredictions();

    const hasFilesUploading = useMemo(() => {
        return (
            pendingMediaItems.length > 0 ||
            mediaItems.some((item) => {
                const extractedFrameIds = getExtractedFrameIdsForMediaItem(item);
                const uploadingExtractedFrames = extractedFrameIds.some(
                    (frameId) => media[frameId]?.status === MediaItemStatus.UPLOADING
                );

                return (
                    item.status === MediaItemStatus.UPLOADING ||
                    item.status === MediaItemStatus.PROCESSING ||
                    uploadingExtractedFrames
                );
            })
        );
    }, [media, pendingMediaItems, mediaItems]);

    const isCurrentFrameExtracting = useMemo(() => {
        if (!currentMedia?.file?.isVideo || currentMedia?.frameSegment === undefined) return false;
        const file = media[currentMedia.file.id] ?? currentMedia.file;
        const frameId = file.segmentToFrameMediaItemIdMap?.[currentMedia.frameSegment];
        if (!frameId) return false;
        const frame = media[frameId];
        const status = frame?.status;
        return status === MediaItemStatus.UPLOADING || status === MediaItemStatus.PROCESSING;
    }, [currentMedia?.file, currentMedia?.frameSegment, media]);

    const isAutoPredictionWaitingForUpload = useMemo(() => {
        if (!isAutoPredictionPending) return false;
        const mediaItemForInference = currentMedia?.file?.isVideo
            ? currentMedia?.frame
            : currentMedia?.file;
        return (
            mediaItemForInference != null &&
            mediaItemForInference.status !== MediaItemStatus.COMPLETED
        );
    }, [isAutoPredictionPending, currentMedia?.file, currentMedia?.frame]);

    const isInPromptStep = flow === Flows.BUILD && step === BuildSteps.PROMPT;

    const isWaiting =
        isInPromptStep &&
        (hasFilesUploading ||
            isGeneratingPredictions ||
            isExtractingWithAI ||
            isAutoPredictionPending);

    const isExtractingFrame =
        isCurrentFrameExtracting ||
        (isAutoPredictionWaitingForUpload && !!currentMedia?.file?.isVideo);
    const isProcessing =
        hasFilesUploading || (isAutoPredictionWaitingForUpload && !currentMedia?.file?.isVideo);
    const loadingMessage = useMemo((): string => {
        if (!isInPromptStep) return "Generating...";
        if (isExtractingFrame) return "Extracting frame...";
        if (isProcessing) return "Processing...";
        if (isGeneratingPredictions) return "Finding objects...";
        if (isAutoPredictionPending) return "Preparing...";
        if (isExtractingWithAI) return "Analyzing...";
        return "Finding objects...";
    }, [
        isInPromptStep,
        isExtractingFrame,
        isProcessing,
        isGeneratingPredictions,
        isAutoPredictionPending,
        isExtractingWithAI
    ]);

    return { isWaiting, loadingMessage };
};
